import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Product, productService, RelationshipType } from '@/services/productService';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, Plus, Sparkles, Info, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { debounce } from 'lodash';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { pickPrimaryImage } from '@/utils/images';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { formatNumberForDisplay, type NumberFormat } from '@/utils/numberFormat';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { logError } from '@/utils/errorHandler';

interface EnhancedAddRelatedProductPanelProps {
  productId: number;
  relatedProducts: Product[];
  onProductAdded: (product: Product, relationshipType: RelationshipType, notes?: string) => void;
  onMultipleProductsAdded: (
    productIds: number[],
    relationshipType: RelationshipType,
    notes?: string
  ) => Promise<{
    success: boolean;
    processed: number;
    failed: number;
  } | void>;
}

export const EnhancedAddRelatedProductPanel: React.FC<EnhancedAddRelatedProductPanelProps> = ({
  productId,
  relatedProducts,
  onProductAdded,
  onMultipleProductsAdded,
}) => {
  // i18n Step 1: Get translations hook
  const { getTabsLabels, getAddPanelNotification } = useProductDetailPageTranslations();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [results, setResults] = useState<Product[]>([]);
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState<boolean>(false);
  const [addingProduct, setAddingProduct] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [selectedRelationshipType, setSelectedRelationshipType] =
    useState<RelationshipType>('general');
  const [notes, setNotes] = useState('');
  const [showNotesField, setShowNotesField] = useState(false);

  // Create a ref for the search input
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get organization settings for number formatting
  const { orgSettings } = useOrgSettings();
  const numberFormat = (orgSettings?.number_format || 'dot') as NumberFormat;

  // Helper function to format currency with organization preferences
  const formatCurrencyWithOrgFormat = (
    amount: number | string | null | undefined,
    currency: string = 'USD'
  ) => {
    // Handle different input types and edge cases
    let numericAmount: number;

    if (amount === null || amount === undefined || amount === '') {
      return 'N/A';
    } else if (typeof amount === 'string') {
      const parsed = parseFloat(amount);
      numericAmount = isNaN(parsed) ? 0 : parsed;
    } else if (typeof amount === 'number') {
      numericAmount = isNaN(amount) ? 0 : amount;
    } else {
      return 'N/A';
    }

    const formattedNumber = formatNumberForDisplay(numericAmount, numberFormat, 2);
    return `${currency} ${formattedNumber}`;
  };

  // Helper function to get price display
  const getPriceDisplay = (product: Product) => {
    // Check if product has prices array
    if (product.prices && Array.isArray(product.prices) && product.prices.length > 0) {
      const firstPrice = product.prices[0];
      // Handle ProductPrice structure with amount and currency fields
      if (firstPrice.amount !== undefined && firstPrice.currency) {
        return formatCurrencyWithOrgFormat(firstPrice.amount, firstPrice.currency);
      }
    }

    // Check if product has a single price field (legacy format)
    if (product.price !== undefined && product.price !== null) {
      const currency = product.currency || orgSettings?.default_currency?.iso_code || 'USD';
      return formatCurrencyWithOrgFormat(product.price, currency);
    }

    return 'N/A';
  };

  const loadAlgorithmicSuggestions = useCallback(async () => {
    if (!productId) return;

    setIsSuggestionsLoading(true);

    try {
      // This would be a call to an endpoint that returns AI/algorithm-suggested products
      // For now we'll simulate by getting products from the same category
      // In a real implementation, you'd have a dedicated endpoint like:
      // const suggestedProducts = await productService.getProductSuggestions(productId);

      // Simulating with a search for now
      const allProducts = await productService.searchProducts('', 20);

      // Filter out current product only - related products filtering happens in effect
      const filteredSuggestions = allProducts
        .filter(product => product.id !== productId)
        .slice(0, 10); // Get more initially, filter related products later

      setSuggestions(filteredSuggestions);
    } catch (error) {
      logError(
        'Failed to load product suggestions',
        {
          component: 'EnhancedAddRelatedProductPanel',
          action: 'loadAlgorithmicSuggestions',
          metadata: {
            productId,
            error,
          },
        },
        {
          showToast: true,
          toastMessage: 'Failed to load suggestions',
          userFacing: true,
        }
      );
    } finally {
      setIsSuggestionsLoading(false);
    }
  }, [productId]);

  // Load algorithmic suggestions on mount
  useEffect(() => {
    if (productId) {
      loadAlgorithmicSuggestions();
    }
  }, [productId, loadAlgorithmicSuggestions]);

  // Filter out related products from suggestions when relatedProducts changes
  useEffect(() => {
    setSuggestions(
      prev =>
        prev
          .filter(product => !relatedProducts.some(related => related.id === product.id))
          .slice(0, 5) // Limit to 5 suggestions after filtering
    );
  }, [relatedProducts]);

  // Filter out related products from search results when relatedProducts changes
  useEffect(() => {
    setResults(prev =>
      prev.filter(product => !relatedProducts.some(related => related.id === product.id))
    );
  }, [relatedProducts]);

  // Create a debounced search function

  const debouncedSearch = useRef(
    debounce(async (term: string) => {
      if (!term || term.length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Using the dedicated searchProducts function
        const products = await productService.searchProducts(term);

        // Filter out the current product only - related products filtering handled by separate effect
        const filteredProducts = products.filter(product => product.id !== productId);

        setResults(filteredProducts.slice(0, 10)); // Limit to 10 results
      } catch (error) {
        logError(
          'Failed to search products',
          {
            component: 'EnhancedAddRelatedProductPanel',
            action: 'searchProducts',
            metadata: {
              searchTerm: term,
              productId,
              error,
            },
          },
          {
            showToast: true,
            toastMessage: getAddPanelNotification('searchFailed'),
            userFacing: true,
          }
        );
        setError('SEARCH_FAILED');
      } finally {
        setIsLoading(false);
      }
    }, 300)
  ).current;

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Handle product selection
  const handleSelectProduct = (product: Product, checked: boolean) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, product]);
    } else {
      setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
    }
  };

  // Handle adding a suggestion
  const handleAddSuggestion = async (product: Product) => {
    setAddingProduct(true);

    try {
      await onProductAdded(product, selectedRelationshipType, notes);

      // Remove from suggestions
      setSuggestions(prev => prev.filter(p => p.id !== product.id));

      // Show success message
      toast.success(getAddPanelNotification('suggestedProductAdded'));

      // Reset notes
      setNotes('');
      setShowNotesField(false);
    } catch (error) {
      logError(
        'Failed to add suggested product',
        {
          component: 'EnhancedAddRelatedProductPanel',
          action: 'addSuggestedProduct',
          metadata: {
            productId: product.id,
            productName: product.name,
            relationshipType: selectedRelationshipType,
            error,
          },
        },
        {
          showToast: true,
          toastMessage: getAddPanelNotification('suggestedProductAddFailed'),
          userFacing: true,
        }
      );
    } finally {
      setAddingProduct(false);
    }
  };

  // Handle adding multiple selected products
  const handleAddSelectedProducts = async () => {
    if (!selectedProducts.length) return;

    setAddingProduct(true);

    try {
      // Extract IDs for bulk operation
      const productIds = selectedProducts.map(p => p.id!);

      // Call the handler function
      const result = await onMultipleProductsAdded(productIds, selectedRelationshipType, notes);

      // Reset selection and search
      setSelectedProducts([]);
      setSearchTerm('');
      setResults([]);
      setNotes('');
      setShowNotesField(false);

      // Show success message based on result if available
      if (result && 'processed' in result) {
        toast.success(
          getAddPanelNotification('multipleProductsAdded', {
            count: result.processed,
          })
        );
      }
    } catch (error) {
      logError(
        'Failed to add multiple products',
        {
          component: 'EnhancedAddRelatedProductPanel',
          action: 'addMultipleProducts',
          metadata: {
            productIds: selectedProducts.map(p => p.id),
            productCount: selectedProducts.length,
            relationshipType: selectedRelationshipType,
            error,
          },
        },
        {
          showToast: true,
          toastMessage: getAddPanelNotification('multipleProductsAddFailed'),
          userFacing: true,
        }
      );
    } finally {
      setAddingProduct(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextElement = document.getElementById(`product-result-${index + 1}`);
      if (nextElement) nextElement.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevElement = document.getElementById(`product-result-${index - 1}`);
      if (prevElement) prevElement.focus();
      else searchInputRef.current?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Toggle selection
      const product = results[index];
      const isSelected = selectedProducts.some(p => p.id === product.id);
      handleSelectProduct(product, !isSelected);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setResults([]);
      searchInputRef.current?.focus();
    }
  };

  // Toggle notes field
  const toggleNotesField = () => {
    setShowNotesField(prev => !prev);
  };

  return (
    <div className="mb-6 rounded-md border border-input bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium">
          {getTabsLabels.actions.relatedProducts.addPanel.title}
        </h3>

        <div className="flex items-center space-x-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Info className="size-4 text-muted-foreground" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" align="center" className="max-w-sm">
                <p>{getTabsLabels.actions.relatedProducts.addPanel.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Select
            value={selectedRelationshipType}
            onValueChange={(value: RelationshipType) => setSelectedRelationshipType(value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue
                placeholder={
                  getTabsLabels.actions.relatedProducts.addPanel.form.placeholders.relationshipType
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>
                  {getTabsLabels.actions.relatedProducts.addPanel.form.labels.relationshipType}
                </SelectLabel>
                <SelectItem value="general">
                  {getTabsLabels.actions.relatedProducts.relationshipTypes.general}
                </SelectItem>
                <SelectItem value="accessory">
                  {getTabsLabels.actions.relatedProducts.relationshipTypes.accessory}
                </SelectItem>
                <SelectItem value="variant">
                  {getTabsLabels.actions.relatedProducts.relationshipTypes.variant}
                </SelectItem>
                <SelectItem value="frequently_bought_together">
                  {
                    getTabsLabels.actions.relatedProducts.relationshipTypes
                      .frequently_bought_together
                  }
                </SelectItem>
                <SelectItem value="replacement">
                  {getTabsLabels.actions.relatedProducts.relationshipTypes.replacement}
                </SelectItem>
                <SelectItem value="similar">
                  {getTabsLabels.actions.relatedProducts.relationshipTypes.similar}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={toggleNotesField}
            className={showNotesField ? 'bg-primary/10' : ''}
            title={getTabsLabels.actions.relatedProducts.addPanel.form.buttons.addNotes}
          >
            <MessageSquare className="size-4" />
          </Button>
        </div>
      </div>

      {showNotesField && (
        <div className="mb-4 mt-2">
          <Label htmlFor="notes" className="mb-1.5 block text-sm font-medium">
            {getTabsLabels.actions.relatedProducts.addPanel.form.labels.notes}
          </Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={getTabsLabels.actions.relatedProducts.addPanel.form.placeholders.notes}
            className="resize-none"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {getTabsLabels.actions.relatedProducts.addPanel.notes.help}
          </p>
        </div>
      )}

      <div className="mb-2 flex space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder={getTabsLabels.actions.relatedProducts.addPanel.form.placeholders.search}
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-9"
            aria-label={getTabsLabels.actions.relatedProducts.addPanel.form.aria.searchProducts}
          />
          {isLoading && (
            <Loader2 className="absolute right-2.5 top-2.5 size-4 animate-spin text-muted-foreground" />
          )}
        </div>

        <Button
          onClick={handleAddSelectedProducts}
          disabled={selectedProducts.length === 0 || addingProduct}
          size="sm"
          className="whitespace-nowrap"
          aria-label={getTabsLabels.actions.relatedProducts.addPanel.form.aria.addSelectedProducts}
        >
          {addingProduct ? <Loader2 className="mr-1 size-4 animate-spin" /> : null}
          {getTabsLabels.actions.relatedProducts.addPanel.form.buttons.addSelected.replace(
            '{{count}}',
            selectedProducts.length.toString()
          )}
        </Button>
      </div>

      {error && (
        <p className="mt-1 text-sm text-destructive">
          {error === 'SEARCH_FAILED' ? 'Unable to search products' : error}
        </p>
      )}

      {results.length > 0 && (
        <div className="mb-4">
          <ScrollArea className="mt-1 max-h-64 overflow-auto rounded-md border">
            <div className="p-1">
              {results.map((product, index) => {
                const isSelected = selectedProducts.some(p => p.id === product.id);

                return (
                  <div
                    id={`product-result-${index}`}
                    key={product.id}
                    className={`
                      flex items-center rounded-sm p-2 text-sm
                      ${isSelected ? 'bg-primary/10' : 'hover:bg-accent'}
                      cursor-pointer
                    `}
                    tabIndex={0}
                    role="option"
                    aria-selected={isSelected}
                    onKeyDown={e => handleKeyDown(e, index)}
                    onClick={() => handleSelectProduct(product, !isSelected)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={checked => handleSelectProduct(product, !!checked)}
                      className="mr-2"
                      id={`checkbox-${product.id}`}
                      onClick={e => e.stopPropagation()}
                    />

                    <HoverCard openDelay={300}>
                      <HoverCardTrigger asChild>
                        <div className="flex min-w-0 flex-1 cursor-pointer items-center justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{product.name}</div>
                            <div className="truncate text-xs text-muted-foreground">
                              {getTabsLabels.actions.relatedProducts.addPanel.results.productCard.sku.replace(
                                '{{sku}}',
                                product.sku || ''
                              )}
                            </div>
                          </div>

                          <div className="ml-2 flex items-center space-x-2">
                            {product.category && (
                              <Badge variant="outline" className="ml-2 whitespace-nowrap">
                                {typeof product.category === 'string'
                                  ? product.category
                                  : Array.isArray(product.category)
                                    ? product.category.length > 0
                                      ? product.category[product.category.length - 1].name
                                      : ''
                                    : product.category.name || ''}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </HoverCardTrigger>

                      <HoverCardContent side="right" align="start" className="w-80">
                        <div className="flex space-x-4">
                          {pickPrimaryImage(product) && (
                            <div className="size-20 shrink-0 overflow-hidden rounded-md bg-muted">
                              <img
                                src={pickPrimaryImage(product)}
                                alt={product.name}
                                className="size-full object-contain"
                              />
                            </div>
                          )}

                          <div className="min-w-0 flex-1 space-y-1">
                            <h4 className="text-sm font-semibold">{product.name}</h4>
                            <p className="text-sm">
                              {getTabsLabels.actions.relatedProducts.addPanel.results.productCard.sku.replace(
                                '{{sku}}',
                                product.sku || ''
                              )}
                            </p>
                            <p className="text-sm">
                              {getTabsLabels.actions.relatedProducts.addPanel.results.productCard.price.replace(
                                '{{price}}',
                                getPriceDisplay(product)
                              )}
                            </p>
                            {product.description && (
                              <p className="line-clamp-2 text-xs text-muted-foreground">
                                {product.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}

      {searchTerm.length > 1 && results.length === 0 && !isLoading && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          {getTabsLabels.actions.relatedProducts.addPanel.results.noResults}
        </div>
      )}

      {/* Algorithmic suggestions */}
      {suggestions.length > 0 && !searchTerm && (
        <div className="mt-4">
          <div className="mb-2 flex items-center gap-2">
            <Sparkles className="size-4 text-blue-500" />
            <h4 className="text-sm font-medium">
              {getTabsLabels.actions.relatedProducts.addPanel.suggestions.title}
            </h4>
          </div>

          <div className="mb-2 text-xs text-muted-foreground">
            {getTabsLabels.actions.relatedProducts.addPanel.suggestions.description}
          </div>

          <ScrollArea className="max-h-48 overflow-auto rounded-md border">
            <div className="p-1">
              {suggestions.map(product => (
                <div
                  key={`suggestion-${product.id}`}
                  className="flex cursor-pointer items-center justify-between rounded-sm p-2 text-sm hover:bg-accent"
                  onClick={() => handleAddSuggestion(product)}
                >
                  <HoverCard openDelay={300}>
                    <HoverCardTrigger asChild>
                      <div className="min-w-0 flex-1 cursor-pointer">
                        <div className="truncate font-medium">{product.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {getTabsLabels.actions.relatedProducts.addPanel.results.productCard.sku.replace(
                            '{{sku}}',
                            product.sku || ''
                          )}
                        </div>
                      </div>
                    </HoverCardTrigger>

                    <HoverCardContent side="right" align="start" className="w-80">
                      <div className="flex space-x-4">
                        {pickPrimaryImage(product) && (
                          <div className="size-20 shrink-0 overflow-hidden rounded-md bg-muted">
                            <img
                              src={pickPrimaryImage(product)}
                              alt={product.name}
                              className="size-full object-contain"
                            />
                          </div>
                        )}

                        <div className="min-w-0 flex-1 space-y-1">
                          <h4 className="text-sm font-semibold">{product.name}</h4>
                          <p className="text-sm">
                            {getTabsLabels.actions.relatedProducts.addPanel.results.productCard.sku.replace(
                              '{{sku}}',
                              product.sku || ''
                            )}
                          </p>
                          <p className="text-sm">
                            {getTabsLabels.actions.relatedProducts.addPanel.results.productCard.price.replace(
                              '{{price}}',
                              getPriceDisplay(product)
                            )}
                          </p>
                          {product.description && (
                            <p className="line-clamp-2 text-xs text-muted-foreground">
                              {product.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>

                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={e => {
                      e.stopPropagation();
                      handleAddSuggestion(product);
                    }}
                    disabled={addingProduct}
                    className="size-7 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                  >
                    <Plus className="size-4" />
                    <span className="sr-only">
                      {getTabsLabels.actions.relatedProducts.addPanel.form.aria.addSuggestedProduct}
                    </span>
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {isSuggestionsLoading && !searchTerm && (
        <div className="mt-4 flex items-center justify-center p-4">
          <Loader2 className="mr-2 size-4 animate-spin" />
          <span className="text-sm text-muted-foreground">
            {getTabsLabels.actions.relatedProducts.addPanel.suggestions.loading}
          </span>
        </div>
      )}
    </div>
  );
};

export default EnhancedAddRelatedProductPanel;
